import axios from 'axios';
import { Storage } from './Storage';

const LODGING_API_KEY = 'lodging_api_key';
const LODGING_TENANT_ID = 'lodging_tenant_id';
const LODGING_NAME = 'lodging_name';

export default class LodgingIntegration {
    /**
     * Check if the store is lodge integrated
     * @param {string} storeDb - The store database identifier
     * @returns {Promise<Object|null>} - Returns lodging configuration or null if not integrated
     */
    static async checkLodgeIntegration(storeDb) {
        try {
            const response = await axios.get(`${window.serverApi}lodging/api-key/${storeDb}`);
            return response.data;
        } catch (error) {
            console.error('Error checking lodge integration:', error);
            return null;
        }
    }

    /**
     * Save lodging configuration to storage
     * @param {Object} config - Lodging configuration object
     */
    static async saveLodgingConfig(config) {
        await Storage.setLodgingApiKey(config.apiKey);
        await Storage.setLodgingTenantId(config.tenantId);
        await Storage.setLodgingName(config.lodgeName);
    }

    /**
     * Get API key from storage
     * @returns {Promise<string|null>}
     */
    static async getApiKey() {
        return await Storage.getLodgingApiKey();
    }

    /**
     * Get tenant ID from storage
     * @returns {Promise<string|null>}
     */
    static async getTenantId() {
        return await Storage.getLodgingTenantId();
    }

    /**
     * Get lodge name from storage
     * @returns {Promise<string|null>}
     */
    static async getLodgeName() {
        return await Storage.getLodgingName();
    }

    /**
     * Check if lodge integration is active
     * @returns {Promise<boolean>}
     */
    static async isLodgeIntegrated() {
        const apiKey = await this.getApiKey();
        return !!apiKey;
    }

    /**
     * Get list of occupied rooms
     * @returns {Promise<Array>} - List of occupied rooms
     */
    static async getOccupiedRooms() {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.get(`${window.lodgingApi}rooms/occupied`, {
                headers: {
                    'X-API-KEY': apiKey,
                    'X-Tenant-ID': tenantId
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching occupied rooms:', error);
            throw error;
        }
    }

    /**
     * Get list of all rooms
     * @returns {Promise<Array>} - List of all rooms
     */
    static async getAllRooms() {
        return this.getOccupiedRooms();
    }

    /**
     * Get billing summary for a booking
     * @param {number} bookingId - The booking ID
     * @returns {Promise<Object>} - Billing details (BillDetailsDTO)
     */
    static async getBookingDetails(bookingId) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.get(`${window.lodgingApi}bookings/${bookingId}/details`, {
                headers: {
                    'X-API-KEY': apiKey,
                    'X-Tenant-ID': tenantId
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error fetching booking details:', error);
            throw error;
        }
    }

    /**
     * Check if a charge fits within a booking's service budget
     * @param {number} bookingId - The booking ID
     * @param {number} amount - The charge amount to check
     * @returns {Promise<Object|null>} - { allowed, remainingLimit } or null on error
     */
    static async checkServiceBudget(bookingId, amount) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.post(
                `${window.lodgingApi}bookings/${bookingId}/charges/can-apply`,
                { amount },
                {
                    headers: {
                        'X-API-KEY': apiKey,
                        'X-Tenant-ID': tenantId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error checking service budget:', error);
            return null;
        }
    }

    /**
     * Add a completed charge to a booking
     * @param {number} bookingId - The booking ID
     * @param {Object} chargeData - Charge details { description, amount, itemType, quantity, discountAmount, invoiceRef, billTotal, tipAmount }
     * @returns {Promise<Object>} - Result
     */
    static async addRoomCharge(bookingId, chargeData) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            console.log('[LodgingIntegration] addRoomCharge payload:', chargeData);
            const response = await axios.post(
                `${window.lodgingApi}bookings/${bookingId}/charges`,
                chargeData,
                {
                    headers: {
                        'X-API-KEY': apiKey,
                        'X-Tenant-ID': tenantId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error adding room charge:', error);
            throw error;
        }
    }

    /**
     * Initiate a secure (2FA) charge
     * @param {number} bookingId - The booking ID
     * @param {Object} chargeData - Charge details { description, amount, itemType, invoiceRef }
     * @returns {Promise<Object>} - { transactionId, message }
     */
    static async initiateSecureCharge(bookingId, chargeData) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.post(
                `${window.lodgingApi}bookings/${bookingId}/charges/secure/initiate`,
                chargeData,
                {
                    headers: {
                        'X-API-KEY': apiKey,
                        'X-Tenant-ID': tenantId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error initiating secure charge:', error);
            throw error;
        }
    }

    /**
     * Verify a secure charge with 2FA code
     * @param {string} transactionId - The transaction ID from initiate
     * @param {string} code - The 2FA code
     * @returns {Promise<Object>} - Verification result
     */
    static async verifySecureCharge(transactionId, code) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.post(
                `${window.lodgingApi}charges/secure/verify`,
                { transactionId, code },
                {
                    headers: {
                        'X-API-KEY': apiKey,
                        'X-Tenant-ID': tenantId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error verifying secure charge:', error);
            throw error;
        }
    }

    /**
     * Process a payment for a booking
     * @param {number} bookingId - The booking ID
     * @param {Object} paymentData - Payment information { amount, paymentMethod, transactionReference, notes }
     * @returns {Promise<Object>} - Payment result
     */
    static async processPayment(bookingId, paymentData) {
        try {
            const apiKey = await this.getApiKey();
            const tenantId = await this.getTenantId();

            if (!apiKey || !tenantId) {
                throw new Error('Lodging integration not configured');
            }

            const response = await axios.post(
                `${window.lodgingApi}bookings/${bookingId}/payments`,
                paymentData,
                {
                    headers: {
                        'X-API-KEY': apiKey,
                        'X-Tenant-ID': tenantId,
                        'Content-Type': 'application/json'
                    }
                }
            );

            return response.data;
        } catch (error) {
            console.error('Error processing payment:', error);
            throw error;
        }
    }
}
